import { z } from 'zod';

// src/overlays/model.ts
var clean = (v) => v == null || v === "" ? void 0 : v;
function mapWireScope(scope) {
  return {
    scope: scope.scope,
    tierWeight: scope.tier_weight ?? 0,
    overrides: scope.overrides ?? {}
  };
}
function mapWireHit(hit) {
  return {
    kind: hit.kind,
    catalogId: hit.catalog_id,
    name: clean(hit.name),
    color: clean(hit.color),
    logo: clean(hit.logo),
    tierWeight: hit.tier_weight ?? 0,
    isPrimary: Boolean(hit.is_primary),
    severity: clean(hit.severity),
    riskLevel: clean(hit.risk_level),
    confidence: hit.confidence ?? void 0,
    lifecycle: clean(hit.lifecycle),
    explanation: hit.explanation ?? [],
    availableScopes: (hit.available_scopes ?? []).map(mapWireScope)
  };
}
function mapWireEntity(entity) {
  return {
    id: entity.id,
    name: clean(entity.name),
    type: clean(entity.type),
    logo: clean(entity.logo),
    color: clean(entity.color),
    confidence: entity.confidence ?? void 0,
    verified: entity.verified ?? void 0
  };
}
function catalogBlockToResolvedOverlays(block, ctx) {
  const b = block ?? {};
  const primary = Object.entries(b.primary ?? {}).map(([kind, catalogId]) => ({
    kind,
    catalogId: String(catalogId)
  }));
  return {
    address: ctx.address,
    chainId: ctx.chainId,
    asOf: ctx.asOf ?? Math.floor(Date.now() / 1e3),
    entities: (b.entities ?? []).map(mapWireEntity),
    tags: (b.tags ?? []).map(mapWireHit),
    labels: (b.labels ?? []).map(mapWireHit),
    groups: (b.groups ?? []).map(mapWireHit),
    primary
  };
}
function toResolvedOverlays(bundle) {
  return { ...bundle, entities: bundle.entities };
}
function isEmptyOverlays(bundle) {
  if (!bundle) return true;
  return bundle.entities.length === 0 && bundle.tags.length === 0 && bundle.labels.length === 0 && bundle.groups.length === 0;
}

// src/precedence/enums.generated.ts
var Severity = {
  INFO: "info",
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  CRITICAL: "critical"
};
var RiskLevel = {
  SAFE: "safe",
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  SEVERE: "severe"
};

// src/precedence/risk-ladder.generated.json
var risk_ladder_generated_default = {
  bands: {
    high: 60,
    low: 20,
    medium: 40,
    severe: 85
  },
  level_by_severity: {
    critical: "severe",
    high: "high",
    low: "low",
    medium: "medium"
  },
  scale_top: 100,
  score_by_level: {
    high: 72.5,
    low: 30,
    medium: 50,
    safe: 10,
    severe: 92.5
  },
  score_by_severity: {
    critical: 92.5,
    high: 72.5,
    low: 30,
    medium: 50
  },
  severity_by_level: {
    high: "high",
    low: "low",
    medium: "medium",
    severe: "critical"
  }
};

// src/precedence/risk-ladder.ts
var LadderSchema = z.object({
  scale_top: z.number(),
  bands: z.object({
    severe: z.number(),
    high: z.number(),
    medium: z.number(),
    low: z.number()
  }),
  level_by_severity: z.record(z.string(), z.string()),
  severity_by_level: z.record(z.string(), z.string()),
  score_by_level: z.record(z.string(), z.number()),
  score_by_severity: z.record(z.string(), z.number())
});
var SNAPSHOT = LadderSchema.parse(risk_ladder_generated_default);
SNAPSHOT.scale_top;
var RISK_SCORE_BANDS = Object.freeze({ ...SNAPSHOT.bands });
var LEVEL_BY_SEVERITY = Object.freeze(
  SNAPSHOT.level_by_severity
);
var SEVERITY_BY_LEVEL = Object.freeze(
  SNAPSHOT.severity_by_level
);
var SCORE_BY_SEVERITY = Object.freeze(
  SNAPSHOT.score_by_severity
);
var SCORE_BY_LEVEL = Object.freeze(SNAPSHOT.score_by_level);

// src/precedence/risk.ts
var RISK_LEVEL_ORDER = [
  RiskLevel.SAFE,
  RiskLevel.LOW,
  RiskLevel.MEDIUM,
  RiskLevel.HIGH,
  RiskLevel.SEVERE
];
var RISK_LEVEL_RANK = Object.freeze(
  Object.fromEntries(RISK_LEVEL_ORDER.map((level, i) => [level, i]))
);
var RISK_VALUES = new Set(RISK_LEVEL_ORDER);
function coerceRisk(value) {
  const s = typeof value === "string" ? value.toLowerCase() : "";
  return RISK_VALUES.has(s) ? s : void 0;
}
function worstBy(items, map, levelOf) {
  let worst;
  let worstRank = -1;
  for (const item of items) {
    const mapped = map(item);
    if (mapped === void 0) continue;
    const rank = RISK_LEVEL_RANK[levelOf(mapped)] ?? 0;
    if (rank > worstRank) {
      worst = mapped;
      worstRank = rank;
    }
  }
  return worst;
}
var DEFAULT_RISK_SCORE_BANDS = RISK_SCORE_BANDS;
function riskLevelFromScore(score, bands = DEFAULT_RISK_SCORE_BANDS) {
  if (score == null || !Number.isFinite(score)) return void 0;
  if (score >= bands.severe) return RiskLevel.SEVERE;
  if (score >= bands.high) return RiskLevel.HIGH;
  if (score >= bands.medium) return RiskLevel.MEDIUM;
  if (score >= bands.low) return RiskLevel.LOW;
  return RiskLevel.SAFE;
}

// src/addresses/model.ts
var ADDRESS_FIELD_DEFAULTS = {
  address_type: "unknown",
  risk_level: "safe",
  severity: "info",
  lifecycle: "candidate",
  is_primary_chain: false,
  is_human_approved: false,
  system_default: false,
  confidence: 1
};
function isDefaultedField(row, field) {
  const value = row?.[field];
  if (value === null || value === void 0) return true;
  const fallback = ADDRESS_FIELD_DEFAULTS[field];
  if (typeof value === "string" && typeof fallback === "string") {
    return value.trim().toLowerCase() === fallback;
  }
  return value === fallback;
}

// src/overlays/risk.ts
function wireKey(value) {
  return typeof value === "string" ? value.toLowerCase().trim() : "";
}
function riskFromSeverity(severity) {
  return LEVEL_BY_SEVERITY[wireKey(severity)];
}
function scoreFromSeverity(severity) {
  return SCORE_BY_SEVERITY[wireKey(severity)];
}
function declaredRisk(value) {
  const key = wireKey(value);
  return SEVERITY_BY_LEVEL[key] ? key : void 0;
}
function riskForHit(severity, riskLevel) {
  const stated = riskFromSeverity(severity);
  const declared = declaredRisk(riskLevel);
  const level = stated && declared ? (RISK_LEVEL_RANK[declared] ?? 0) > (RISK_LEVEL_RANK[stated] ?? 0) ? declared : stated : stated ?? declared;
  if (!level) return void 0;
  return { level, score: SCORE_BY_LEVEL[level] ?? 0 };
}
function hitRisk(hit) {
  return riskForHit(hit.severity, hit.riskLevel);
}
function overlayRisk(hits) {
  return worstBy(hits, hitRisk, (risk) => risk.level);
}
function catalogRisk(overlays) {
  if (!overlays) return void 0;
  return overlayRisk([...overlays.tags, ...overlays.labels, ...overlays.groups ?? []]);
}
function addressRisk(rows, catalog) {
  const candidates = [];
  for (const row of rows) {
    if (isDefaultedField(row, "risk_level")) {
      continue;
    }
    const level = declaredRisk(row?.risk_level);
    if (level) {
      candidates.push({ level, score: SCORE_BY_LEVEL[level] ?? 0 });
    }
  }
  if (catalog) candidates.push(catalog);
  return worstBy(
    candidates,
    (r) => r,
    (r) => r.level
  );
}

// src/overlays/toAddressOverlay.ts
function leading(hits) {
  return hits.find((hit) => hit.isPrimary) ?? hits[0];
}
function primaryEntityId(bundle) {
  const declared = bundle.primary?.find((p) => p.kind === "entity")?.catalogId;
  if (declared && bundle.entities.some((e) => e.id === declared)) return declared;
  return bundle.entities[0]?.id;
}
function toAddressOverlay(bundle, options = {}) {
  if (!bundle) return {};
  const label = leading(bundle.labels)?.name;
  const brand = leading(bundle.labels) ?? leading(bundle.tags);
  const score = typeof options.riskScore === "number" && Number.isFinite(options.riskScore) ? options.riskScore : void 0;
  const rolledUp = overlayRisk([...bundle.tags, ...bundle.labels, ...bundle.groups]);
  const riskLevel = options.riskLevel ?? riskLevelFromScore(score) ?? rolledUp?.level;
  return {
    label,
    primaryEntityId: primaryEntityId(bundle),
    tags: bundle.tags.map((hit) => hit.name).filter((n) => Boolean(n)),
    color: brand?.color,
    logo: brand?.logo,
    riskLevel,
    riskScore: score ?? rolledUp?.score
  };
}

// src/logging/index.ts
var LEVEL_WEIGHT = { debug: 10, info: 20, warn: 30, error: 40 };
var DEBUG_FLAG_KEY = "authe:debug";
function storage() {
  try {
    const s = typeof localStorage !== "undefined" ? localStorage : void 0;
    return s && typeof s.getItem === "function" ? s : void 0;
  } catch {
    return void 0;
  }
}
function isDebugEnabled() {
  try {
    if (typeof location !== "undefined") {
      const param = new URLSearchParams(location.search).get("debug");
      if (param === "1") {
        enableDebug();
        return true;
      }
      if (param === "0") {
        disableDebug();
        return false;
      }
    }
  } catch {
  }
  return storage()?.getItem(DEBUG_FLAG_KEY) === "1";
}
function enableDebug() {
  storage()?.setItem(DEBUG_FLAG_KEY, "1");
}
function disableDebug() {
  storage()?.removeItem(DEBUG_FLAG_KEY);
}
var sinks = [];
var minConsoleLevel = "warn";
function createLogger(namespace) {
  function emit(level, message, context) {
    const record = { level, namespace, message, context, timestamp: Date.now() };
    for (const sink of sinks) {
      try {
        sink(record);
      } catch {
      }
    }
    const threshold = isDebugEnabled() ? LEVEL_WEIGHT.debug : LEVEL_WEIGHT[minConsoleLevel];
    if (LEVEL_WEIGHT[level] < threshold) return;
    const prefix = `[${namespace}]`;
    const fn = level === "debug" ? console.debug : level === "info" ? console.info : level === "warn" ? console.warn : console.error;
    if (context) fn(prefix, message, context);
    else fn(prefix, message);
  }
  return {
    debug: (m, c) => emit("debug", m, c),
    info: (m, c) => emit("info", m, c),
    warn: (m, c) => emit("warn", m, c),
    error: (m, c) => emit("error", m, c),
    child: (suffix) => createLogger(`${namespace}:${suffix}`)
  };
}

// src/chains/family.ts
var ChainFamily = {
  EVM: "evm",
  UTXO: "utxo",
  SOLANA: "solana",
  OTHER: "other"
};

// src/chains/seed-data.ts
var CHAIN_MAPPING = {
  // --- EVM mainnets ---
  "0x1": "eth",
  "0x3d": "etc",
  "0x89": "polygon",
  "0x38": "bsc",
  "0xcc": "opbnb",
  "0xa86a": "avalanche",
  "0xfa": "fantom",
  "0x19": "cronos",
  "0x2a15c308d": "palm",
  "0xa4b1": "arbitrum",
  "0x64": "gnosis",
  "0x15b38": "chiliz",
  "0x2105": "base",
  "0xa": "optimism",
  "0x2019": "klaytn",
  "0xa4ec": "celo",
  "0x8ae": "kava",
  "0x440": "metis",
  "0x63564c40": "harmony",
  "0x171": "pulsechain",
  "0x1388": "mantle",
  // Nesa. A Cosmos-SDK chain with an EVM module, so its Cosmos chain id
  // (`nesa_41443-1`) and its EVM chain id are the same 41443. Ported from
  // `_seed_data.py`, which has carried it since the Hyperlane domain fallback
  // was retired — without this row every spelling of Nesa falls through the
  // registry unresolved and renders as a raw `eip155:41443`.
  "0xa1e3": "nesa",
  "0x505": "moonriver",
  "0x504": "moonbeam",
  "0x144": "zkSync_era",
  "0x44d": "polygon_zkevm",
  "0xe708": "linea",
  "0x82750": "scroll",
  "0x13e31": "blast",
  "0x4e454152": "aurora",
  "0x7a": "fuse",
  "0x2eb": "flow",
  "0x7e4": "ronin",
  "0x46f": "lisk",
  "0x1b58": "zetachain",
  "0x531": "sei",
  "0x8f": "monad",
  "0x2611": "plasma",
  // --- EVM testnets ---
  "0x5": "goerli",
  "0xaa36a7": "sepolia",
  "0x4268": "holesky",
  "0x13881": "mumbai",
  "0x13882": "polygon_amoy",
  "0x61": "bsc_testnet",
  "0x27d8": "gnosis_chiado",
  "0x15b32": "chiliz_spicy",
  "0x14a33": "base_goerli",
  "0x14a34": "base_sepolia",
  "0x1a4": "optimism_goerli",
  "0xaa37dc": "optimism_sepolia",
  "0x66eee": "arbitrum_sepolia",
  "0x3e9": "klaytn_baobab",
  "0xaef3": "celo_alfajores",
  "0x8ad": "kava_testnet",
  "0x24c": "metis_goerli",
  "0x6357d2e0": "harmony_testnet",
  "0x4e454153": "aurora_testnet",
  "0x507": "moonbase",
  "0x118": "zkSync_testnet",
  "0x145": "zkSync_era_testnet",
  "0x12c": "zksync_sepolia",
  "0x7b": "fuse_testnet",
  "0xe704": "linea_testnet",
  "0xe705": "linea_sepolia",
  "0x8274f": "scroll_testnet",
  "0x138b": "mantle_sepolia",
  "0xa0c71fd": "blast_sepolia",
  "0x98a": "polygon_zkevm_cardona",
  "0xfa2": "fantom_testnet",
  "0x106a": "lisk_sepolia",
  "0x221": "flow_testnet",
  "0x7e5": "ronin_saigon",
  "0x1b59": "zetachain_testnet",
  "0x530": "sei_testnet"
};
var TESTNET_CHAINS = [
  "goerli",
  "sepolia",
  "mumbai",
  "bsc_testnet",
  "gnosis_chiado",
  "chiliz_spicy",
  "base_goerli",
  "optimism_goerli",
  "klaytn_baobab",
  "celo_alfajores",
  "kava_testnet",
  "metis_goerli",
  "harmony_testnet",
  "zkSync_testnet",
  "aurora_testnet",
  "moonbase",
  "zkSync_era_testnet",
  "fuse_testnet",
  "linea_testnet",
  "scroll_testnet",
  "holesky",
  "polygon_amoy",
  "base_sepolia",
  "optimism_sepolia",
  "arbitrum_sepolia",
  "linea_sepolia",
  "blast_sepolia",
  "mantle_sepolia",
  "zksync_sepolia",
  "polygon_zkevm_cardona",
  "fantom_testnet",
  "lisk_sepolia",
  "flow_testnet",
  "ronin_saigon",
  "zetachain_testnet",
  "sei_testnet"
];
var LAYER1_CHAINS = [
  "eth",
  "etc",
  "bsc",
  "avalanche",
  "fantom",
  "klaytn",
  "celo",
  "kava",
  "harmony",
  "moonriver",
  "moonbeam",
  "fuse",
  "pulsechain",
  "flow",
  "ronin",
  "sei",
  "monad",
  "zetachain"
];
var LAYER2_CHAINS = [
  "polygon",
  "arbitrum",
  "optimism",
  "metis",
  "base",
  "zkSync_era",
  "linea",
  "scroll",
  "mantle",
  "opbnb",
  "polygon_zkevm",
  "blast",
  "lisk"
];
var SIDECHAINS = ["palm", "gnosis", "cronos", "aurora"];
var CHAIN_CONFIG = {
  "0x1": { token: "ETH", gasUnit: "Gwei", name: "Ethereum" },
  "0x3d": { token: "ETC", gasUnit: "Gwei", name: "Ethereum Classic" },
  "0x89": { token: "MATIC", gasUnit: "Gwei", name: "Polygon" },
  "0x38": { token: "BNB", gasUnit: "Gwei", name: "BNB Chain" },
  "0xa4b1": { token: "ARB", gasUnit: "Gwei", name: "Arbitrum" },
  "0x2105": { token: "ETH", gasUnit: "Gwei", name: "Base" },
  "0xa": { token: "ETH", gasUnit: "Gwei", name: "Optimism" },
  "0xe708": { token: "ETH", gasUnit: "Gwei", name: "Linea" },
  "0xa86a": { token: "AVAX", gasUnit: "nAVAX", name: "Avalanche" },
  "0xfa": { token: "FTM", gasUnit: "Gwei", name: "Fantom" },
  "0x19": { token: "CRO", gasUnit: "Gwei", name: "Cronos" },
  "0x64": { token: "xDAI", gasUnit: "Gwei", name: "Gnosis" },
  "0x15b38": { token: "CHZ", gasUnit: "Gwei", name: "Chiliz" },
  "0x171": { token: "PLS", gasUnit: "Gwei", name: "PulseChain" },
  "0x504": { token: "GLMR", gasUnit: "Gwei", name: "Moonbeam" },
  "0x505": { token: "MOVR", gasUnit: "Gwei", name: "Moonriver" },
  "0x13e31": { token: "ETH", gasUnit: "Gwei", name: "Blast" },
  "0x144": { token: "ETH", gasUnit: "Gwei", name: "zkSync Era" },
  "0x1388": { token: "MNT", gasUnit: "Gwei", name: "Mantle" },
  "0xa1e3": { token: "NES", gasUnit: "Gwei", name: "Nesa" },
  "0xcc": { token: "BNB", gasUnit: "Gwei", name: "opBNB" },
  // Gas on Polygon zkEVM is ETH, not MATIC.
  "0x44d": { token: "ETH", gasUnit: "Gwei", name: "Polygon zkEVM" },
  "0x1b58": { token: "ZETA", gasUnit: "Gwei", name: "ZetaChain" },
  "0x82750": { token: "ETH", gasUnit: "Gwei", name: "Scroll" },
  "0x4e454152": { token: "ETH", gasUnit: "Gwei", name: "Aurora" },
  "0x531": { token: "SEI", gasUnit: "Gwei", name: "Sei" },
  "0x8f": { token: "MON", gasUnit: "Gwei", name: "Monad" },
  "0x3e9": { token: "KLAY", gasUnit: "Gwei", name: "Klaytn Baobab" },
  "0x4e454153": { token: "ETH", gasUnit: "Gwei", name: "Aurora Testnet" },
  "0x8274f": { token: "ETH", gasUnit: "Gwei", name: "Scroll Sepolia" },
  "0x530": { token: "SEI", gasUnit: "Gwei", name: "Sei Testnet" },
  "0x2eb": { token: "FLOW", gasUnit: "FLOW units", name: "Flow" },
  "0x7e4": { token: "RON", gasUnit: "Gwei", name: "Ronin" },
  "0x46f": { token: "LSK", gasUnit: "LSK units", name: "Lisk" },
  "0xaa36a7": { token: "ETH", gasUnit: "Gwei", name: "Sepolia" },
  "0x4268": { token: "ETH", gasUnit: "Gwei", name: "Holesky" },
  "0x13882": { token: "MATIC", gasUnit: "Gwei", name: "Polygon Amoy" },
  "0x61": { token: "tBNB", gasUnit: "Gwei", name: "BSC Testnet" },
  "0x66eee": { token: "ARB", gasUnit: "Gwei", name: "Arbitrum Sepolia" },
  "0x14a34": { token: "ETH", gasUnit: "Gwei", name: "Base Sepolia" },
  "0xaa37dc": { token: "ETH", gasUnit: "Gwei", name: "Optimism Sepolia" },
  "0xe705": { token: "ETH", gasUnit: "Gwei", name: "Linea Sepolia" },
  "0xfa2": { token: "FTM", gasUnit: "Gwei", name: "Fantom Testnet" },
  "0x27d8": { token: "xDAI", gasUnit: "Gwei", name: "Gnosis Chiado" },
  "0x15b32": { token: "CHZ", gasUnit: "Gwei", name: "Chiliz Testnet" },
  "0x507": { token: "DEV", gasUnit: "Gwei", name: "Moonbase" },
  "0xa0c71fd": { token: "ETH", gasUnit: "Gwei", name: "Blast Sepolia" },
  "0x12c": { token: "ETH", gasUnit: "Gwei", name: "zkSync Sepolia" },
  "0x138b": { token: "MNT", gasUnit: "Gwei", name: "Mantle Sepolia" },
  "0x98a": { token: "MATIC", gasUnit: "Gwei", name: "Polygon zkEVM Cardona" },
  "0x1b59": { token: "ZETA", gasUnit: "Gwei", name: "Zetachain Testnet" },
  "0x221": { token: "FLOW", gasUnit: "FLOW units", name: "Flow Testnet" },
  "0x7e5": { token: "RON", gasUnit: "Gwei", name: "Ronin Saigon Testnet" },
  "0x106a": { token: "LSK", gasUnit: "LSK units", name: "Lisk Sepolia" },
  "0x0": { token: "BTC", gasUnit: "Sats/byte", name: "Bitcoin" },
  "0x1399c": { token: "SOL", gasUnit: "Lamports", name: "Solana" },
  "0x162": { token: "DOT", gasUnit: "Plancks", name: "Polkadot" },
  "0x2611": { token: "XPL", gasUnit: "Gwei", name: "Plasma Mainnet" }
};
var EXPLORER_URLS = {
  eth: "https://etherscan.io",
  etc: "https://blockscout.com/etc/mainnet",
  polygon: "https://polygonscan.com",
  bsc: "https://bscscan.com",
  avalanche: "https://snowtrace.io",
  fantom: "https://ftmscan.com",
  arbitrum: "https://arbiscan.io",
  optimism: "https://optimistic.etherscan.io",
  zksync_era: "https://explorer.zksync.io",
  base: "https://basescan.org",
  linea: "https://lineascan.build",
  scroll: "https://scrollscan.com",
  mantle: "https://explorer.mantle.xyz",
  // The EVM-side Blockscout. Nesa also runs a Cosmos explorer at
  // `explorer.nesa.ai`, which cannot resolve an `0x` address or tx hash.
  nesa: "https://explorer-evm.nesa.ai",
  opbnb: "https://opbnbscan.com",
  polygon_zkevm: "https://zkevm.polygonscan.com",
  blast: "https://blastscan.io",
  pulsechain: "https://scan.pulsechain.com",
  aurora: "https://explorer.aurora.dev",
  flow: "https://evm.flowscan.io",
  ronin: "https://app.roninchain.com",
  lisk: "https://blockscout.lisk.com",
  zetachain: "https://explorer.zetachain.com",
  sei: "https://seitrace.com",
  monad: "https://monadexplorer.com",
  goerli: "https://goerli.etherscan.io",
  sepolia: "https://sepolia.etherscan.io",
  mumbai: "https://mumbai.polygonscan.com",
  plasma: "https://plasmascan.to"
};
var TESTNET_TO_MAINNET = {
  goerli: "eth",
  sepolia: "eth",
  mumbai: "polygon",
  bsc_testnet: "bsc",
  gnosis_chiado: "gnosis",
  chiliz_spicy: "chiliz",
  base_goerli: "base",
  optimism_goerli: "optimism",
  klaytn_baobab: "klaytn",
  celo_alfajores: "celo",
  kava_testnet: "kava",
  metis_goerli: "metis",
  harmony_testnet: "harmony",
  zkSync_testnet: "zkSync_era",
  aurora_testnet: "aurora",
  moonbase: "moonbeam",
  zkSync_era_testnet: "zkSync_era",
  fuse_testnet: "fuse",
  linea_testnet: "linea",
  scroll_testnet: "scroll",
  holesky: "eth",
  polygon_amoy: "polygon",
  base_sepolia: "base",
  optimism_sepolia: "optimism",
  arbitrum_sepolia: "arbitrum",
  linea_sepolia: "linea",
  blast_sepolia: "blast",
  mantle_sepolia: "mantle",
  zksync_sepolia: "zkSync_era",
  polygon_zkevm_cardona: "polygon_zkevm",
  fantom_testnet: "fantom",
  lisk_sepolia: "lisk",
  flow_testnet: "flow",
  ronin_saigon: "ronin",
  zetachain_testnet: "zetachain",
  sei_testnet: "sei"
};

// src/chains/registry.ts
var ChainRegistry = class {
  bySlug = /* @__PURE__ */ new Map();
  byHex = /* @__PURE__ */ new Map();
  byCaip2 = /* @__PURE__ */ new Map();
  register(chain, { replace = false } = {}) {
    const existingSlug = this.bySlug.get(chain.slug);
    if (existingSlug && existingSlug !== chain && !replace) {
      throw new Error(
        `Chain slug "${chain.slug}" already registered; pass replace=true to overwrite.`
      );
    }
    if (chain.chainIdHex) {
      const existingHex = this.byHex.get(chain.chainIdHex);
      if (existingHex && existingHex.slug !== chain.slug && !replace) {
        throw new Error(
          `Chain hex ${chain.chainIdHex} already mapped to slug ${existingHex.slug}; pass replace=true to overwrite.`
        );
      }
    }
    this.bySlug.set(chain.slug, chain);
    if (chain.chainIdHex) this.byHex.set(chain.chainIdHex, chain);
    if (chain.caip2) this.byCaip2.set(chain.caip2, chain);
  }
  unregister(slug) {
    const chain = this.bySlug.get(slug);
    if (!chain) return null;
    this.bySlug.delete(slug);
    if (chain.chainIdHex) this.byHex.delete(chain.chainIdHex);
    if (chain.caip2) this.byCaip2.delete(chain.caip2);
    return chain;
  }
  clear() {
    this.bySlug.clear();
    this.byHex.clear();
    this.byCaip2.clear();
  }
  get(slug) {
    return this.bySlug.get(slug) ?? null;
  }
  /** Lookup by EVM hex chain id (auto `0x`-prefixes + lowercases). */
  getByHex(chainIdHex) {
    let normalized = chainIdHex.toLowerCase();
    if (!normalized.startsWith("0x")) normalized = "0x" + normalized.replace(/^0x/, "");
    return this.byHex.get(normalized) ?? null;
  }
  /** Lookup by CAIP-2 chain id (e.g. "eip155:1"). */
  getByCaip2(caip2) {
    return this.byCaip2.get(caip2.trim().toLowerCase()) ?? null;
  }
  /** Resolve by CAIP-2, hex, OR slug. */
  resolve(value) {
    if (!value) return null;
    if (value.includes(":")) {
      const hit = this.getByCaip2(value);
      if (hit) return hit;
    }
    if (value.startsWith("0x")) {
      const hit = this.getByHex(value);
      if (hit) return hit;
    }
    if (/^\d+$/.test(value)) {
      const hit = this.getByHex("0x" + BigInt(value).toString(16));
      if (hit) return hit;
    }
    return this.get(value.toLowerCase());
  }
  byFamily(family) {
    return [...this.bySlug.values()].filter((c) => c.family === family).sort((a, b) => a.slug.localeCompare(b.slug));
  }
  all() {
    return [...this.bySlug.values()].sort((a, b) => a.slug.localeCompare(b.slug));
  }
};
var registry = new ChainRegistry();
function toCanonicalChainId(value) {
  if (!value) return null;
  const chain = registry.resolve(value);
  if (!chain) return value.trim().toLowerCase() || null;
  return chain.caip2 ?? chain.chainIdHex ?? chain.slug;
}
var REORG_DEPTHS_BY_SLUG = {
  eth: 12,
  polygon: 256,
  matic: 256,
  bsc: 15,
  arbitrum: 12,
  optimism: 12,
  base: 12,
  avalanche: 12,
  fantom: 12,
  cronos: 12,
  gnosis: 12,
  linea: 12,
  scroll: 12,
  mantle: 12,
  zksync: 12,
  zksync_era: 12,
  metis: 12,
  celo: 12,
  klaytn: 12,
  moonbeam: 12,
  moonriver: 12,
  harmony: 12,
  aurora: 12,
  fuse: 12,
  palm: 12,
  chiliz: 12,
  kava: 12,
  blast: 12,
  bitcoin: 6,
  solana: 32,
  // Monero's ten-block spend lock is a network rule; Zcash needs many more
  // blocks than Bitcoin for equal work at a 75-second block.
  monero: 10,
  zcash: 24
};
var BLOCK_TIME_BY_SLUG = {
  eth: 12,
  etc: 13,
  polygon: 2,
  matic: 2,
  bsc: 3,
  arbitrum: 0.25,
  optimism: 2,
  base: 2,
  avalanche: 2,
  fantom: 1,
  cronos: 6,
  gnosis: 5,
  linea: 3,
  scroll: 3,
  mantle: 2,
  zksync: 1,
  zksync_era: 1,
  metis: 4,
  celo: 5,
  klaytn: 1,
  moonbeam: 12,
  moonriver: 12,
  harmony: 2,
  aurora: 1,
  fuse: 5,
  blast: 2,
  bitcoin: 600,
  solana: 0.4,
  polkadot: 6,
  monero: 120,
  zcash: 75
};
var EXPLORER_SUPPLEMENT = {
  bitcoin: "https://blockstream.info",
  solana: "https://solscan.io",
  polkadot: "https://polkadot.subscan.io",
  monero: "https://xmrchain.net",
  // Blockchair's base carries the coin as a path segment, which is exactly
  // what the link builder appends to — `/zcash` + `/transaction/<hash>`.
  zcash: "https://blockchair.com/zcash",
  cronos: "https://cronoscan.com",
  gnosis: "https://gnosisscan.io",
  celo: "https://celoscan.io",
  moonbeam: "https://moonscan.io",
  zkSync_era: "https://explorer.zksync.io",
  matic: "https://polygonscan.com"
};
function explorerFor(slug, rawSlug, mainnetSlug) {
  return EXPLORER_URLS[slug] ?? EXPLORER_URLS[rawSlug] ?? EXPLORER_SUPPLEMENT[slug] ?? EXPLORER_SUPPLEMENT[rawSlug] ?? (mainnetSlug ? EXPLORER_URLS[mainnetSlug] ?? EXPLORER_SUPPLEMENT[mainnetSlug] ?? null : null);
}
var BRAND_COLOR_BY_SLUG = {
  eth: "#627EEA",
  etc: "#328332",
  polygon: "#8247E5",
  matic: "#8247E5",
  bsc: "#F0B90B",
  arbitrum: "#28A0F0",
  optimism: "#FF0420",
  base: "#0052FF",
  avalanche: "#E84142",
  fantom: "#1969FF",
  cronos: "#002D74",
  gnosis: "#0B6E4F",
  linea: "#61DFFF",
  scroll: "#EBC28E",
  mantle: "#000000",
  zksync: "#8C8DFC",
  zksync_era: "#8C8DFC",
  metis: "#00DACC",
  celo: "#FCFF52",
  klaytn: "#FF1B1B",
  moonbeam: "#53CBC8",
  moonriver: "#C0C0C0",
  harmony: "#00AEE9",
  aurora: "#70D44B",
  fuse: "#B4F9BA",
  blast: "#FCFC03",
  kava: "#FF433E",
  bitcoin: "#F7931A",
  solana: "#14F195",
  polkadot: "#E6007A",
  monero: "#FF6600",
  zcash: "#F4B728"
};
function brandColorFor(slug, mainnetSlug) {
  return BRAND_COLOR_BY_SLUG[slug] ?? (mainnetSlug ? BRAND_COLOR_BY_SLUG[mainnetSlug] ?? null : null);
}
var LOGO_URL_BY_SLUG = {};
var CAIP2_BY_SLUG = {
  bitcoin: "bip122:000000000019d6689c085ae165831e93",
  solana: "solana:5eykt4usfv8p8njdtrepy1vzqkqzkvdp",
  polkadot: "polkadot:91b171bb158e2d3848fa23a9f1c25182",
  // Zcash is a Bitcoin fork, so `bip122` is its registered namespace and the
  // reference is its genesis hash truncated to 32 chars — Bitcoin's own rule.
  zcash: "bip122:00040fe8ec8471911baa1db1266ea15d",
  // Monero has no CASA-registered namespace; `monero` is a platform-local
  // choice. See the note on the Python side, which this table must match
  // byte for byte — `test_chains_fe_registry_parity.py` fails if it drifts.
  monero: "monero:418015bb9ae982a1975da7d79277c270"
};
function deriveCaip2(slug, family, chainIdHex) {
  if (CAIP2_BY_SLUG[slug]) return CAIP2_BY_SLUG[slug];
  if (family === ChainFamily.EVM && chainIdHex) {
    return `eip155:${BigInt(chainIdHex).toString(10)}`;
  }
  return null;
}
var NON_EVM_OVERRIDES = {
  bitcoin: { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Bitcoin", nativeToken: "BTC" },
  solana: { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana", nativeToken: "SOL" },
  polkadot: { family: ChainFamily.OTHER, decimals: 10, chainIdHex: null, name: "Polkadot", nativeToken: "DOT" },
  monero: { family: ChainFamily.UTXO, decimals: 12, chainIdHex: null, name: "Monero", nativeToken: "XMR" },
  zcash: { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Zcash", nativeToken: "ZEC" }
};
function layerFor(slug) {
  if (LAYER1_CHAINS.includes(slug)) return "l1";
  if (LAYER2_CHAINS.includes(slug)) return "l2";
  if (SIDECHAINS.includes(slug)) return "sidechain";
  return "unknown";
}
function seedFromCanonical() {
  for (const [hexId, rawSlug] of Object.entries(CHAIN_MAPPING)) {
    const slug = rawSlug.toLowerCase();
    const config = CHAIN_CONFIG[hexId];
    const token = config?.token ?? "ETH";
    const override = NON_EVM_OVERRIDES[slug];
    const family = override?.family ?? ChainFamily.EVM;
    const decimals = override?.decimals ?? 18;
    const isTestnet = TESTNET_CHAINS.includes(rawSlug);
    let mainnetSlug = null;
    if (isTestnet) {
      const m = TESTNET_TO_MAINNET[rawSlug];
      if (m) mainnetSlug = m.toLowerCase();
    }
    const reorgDepth = REORG_DEPTHS_BY_SLUG[slug] ?? 12;
    const chainIdHex = hexId.toLowerCase();
    const metadata = { layer: layerFor(slug) };
    if (config?.gasUnit) metadata.gasUnit = config.gasUnit;
    if (slug === "etc") {
      metadata.fork_of = "eth";
      metadata.note = "ETC shares pre-fork tx hashes with ETH. Audit sec. 2 calls out this collision risk.";
    }
    try {
      registry.register(
        {
          slug,
          chainIdHex,
          family,
          name: config?.name ?? rawSlug,
          nativeToken: token,
          decimals,
          isTestnet,
          mainnetSlug,
          reorgDepth,
          explorerUrl: explorerFor(slug, rawSlug, mainnetSlug),
          blockTimeSec: BLOCK_TIME_BY_SLUG[slug] ?? (mainnetSlug ? BLOCK_TIME_BY_SLUG[mainnetSlug] ?? null : null),
          caip2: deriveCaip2(slug, family, chainIdHex),
          brandColor: brandColorFor(slug, mainnetSlug),
          logoUrl: LOGO_URL_BY_SLUG[slug] ?? (mainnetSlug ? LOGO_URL_BY_SLUG[mainnetSlug] ?? null : null),
          metadata
        },
        { replace: true }
      );
    } catch {
      continue;
    }
  }
  for (const [slug, ovr] of Object.entries(NON_EVM_OVERRIDES)) {
    if (registry.get(slug)) continue;
    try {
      registry.register(
        {
          slug,
          chainIdHex: ovr.chainIdHex,
          family: ovr.family,
          name: ovr.name,
          nativeToken: ovr.nativeToken,
          decimals: ovr.decimals,
          isTestnet: false,
          mainnetSlug: null,
          reorgDepth: REORG_DEPTHS_BY_SLUG[slug] ?? 12,
          explorerUrl: explorerFor(slug, slug, null),
          blockTimeSec: BLOCK_TIME_BY_SLUG[slug] ?? null,
          caip2: deriveCaip2(slug, ovr.family, ovr.chainIdHex),
          brandColor: brandColorFor(slug, null),
          logoUrl: LOGO_URL_BY_SLUG[slug] ?? null,
          metadata: {}
        },
        { replace: true }
      );
    } catch {
      continue;
    }
  }
}
seedFromCanonical();

// src/errors/index.ts
var ErrorCategory = {
  VALIDATION: "validation",
  AUTHENTICATION: "authentication",
  AUTHORIZATION: "authorization",
  NOT_FOUND: "not_found",
  CONFLICT: "conflict",
  RATE_LIMIT: "rate_limit",
  EXTERNAL_SERVICE: "external_service",
  DATABASE: "database",
  CONFIGURATION: "configuration",
  INTERNAL: "internal",
  DATA: "data",
  PIPELINE: "pipeline"
};
var DOCS_BASE = "https://docs.authe.io/errors";
function spec(code, description, userAction, category, defaultStatus, opts = {}) {
  return {
    code,
    description,
    userAction,
    documentationUrl: `${DOCS_BASE}/${code}`,
    category,
    defaultStatus,
    retryable: opts.retryable ?? false,
    severity: opts.severity ?? "ERROR"
  };
}
var ErrorCode = {
  // Validation
  VAL001: spec("VAL001", "Missing required field", "Provide all required fields and retry the request.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL002: spec("VAL002", "Invalid field format", "Check the field format documentation and correct the value.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL003: spec("VAL003", "Invalid field value", "Use one of the allowed values listed in the documentation.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL004: spec("VAL004", "Invalid response structure", "An internal response could not be parsed; retry, then contact support if persistent.", ErrorCategory.VALIDATION, 500),
  // Authentication
  AUTH001: spec("AUTH001", "Invalid credentials", "Sign in again with valid credentials.", ErrorCategory.AUTHENTICATION, 401),
  AUTH002: spec("AUTH002", "Token expired", "Refresh your session and retry.", ErrorCategory.AUTHENTICATION, 401),
  AUTH003: spec("AUTH003", "Invalid token", "Sign out, sign back in, and retry.", ErrorCategory.AUTHENTICATION, 401),
  // Authorization
  PERM001: spec("PERM001", "Insufficient permissions", "Ask an admin to grant the required permission, or use a different account.", ErrorCategory.AUTHORIZATION, 403),
  PERM002: spec("PERM002", "Resource access denied", "You do not have access to this resource within your tenant.", ErrorCategory.AUTHORIZATION, 403),
  // Packaging, not permission. PERM001's remedy ("ask an admin to grant the
  // required permission") is actively wrong for a plan refusal: nobody in the
  // customer's own org can grant an entitlement their subscription lacks.
  PERM003: spec("PERM003", "Not included in your plan", "This feature is not part of your current subscription. Contact sales to add it.", ErrorCategory.AUTHORIZATION, 403),
  // A cap, not an absence. PERM003 says the feature is not yours; this says it
  // is, and it is full — so the remedy is to free a slot or move tier, never to
  // retry (which is why this is not the 429 RATE001).
  PERM004: spec("PERM004", "Plan limit reached", "Remove an existing item to free a slot, or upgrade for a higher limit.", ErrorCategory.AUTHORIZATION, 403),
  // Resource
  RES001: spec("RES001", "Resource not found", "Verify the identifier; the resource may have been deleted or never existed.", ErrorCategory.NOT_FOUND, 404),
  RES002: spec("RES002", "Resource already exists", "Use a different identifier or update the existing resource instead.", ErrorCategory.CONFLICT, 409, { severity: "WARNING" }),
  // Rate limiting
  RATE001: spec("RATE001", "Rate limit exceeded", "Slow request rate and retry after the `Retry-After-Ms` interval.", ErrorCategory.RATE_LIMIT, 429, { retryable: true, severity: "WARNING" }),
  // External service
  EXT001: spec("EXT001", "External service unavailable", "An upstream provider is unavailable; retry the request, then contact support if persistent.", ErrorCategory.EXTERNAL_SERVICE, 503, { retryable: true }),
  EXT002: spec("EXT002", "External service timeout", "An upstream provider timed out; retry, then contact support if persistent.", ErrorCategory.EXTERNAL_SERVICE, 504, { retryable: true }),
  // Database
  DB001: spec("DB001", "Database connection error", "Retry the request; if persistent, contact support.", ErrorCategory.DATABASE, 502, { retryable: true }),
  DB002: spec("DB002", "Database query error", "An internal query failed; retry and contact support if persistent.", ErrorCategory.DATABASE, 500),
  DB003: spec("DB003", "Database throttled", "Capacity throttled the request; retry after `Retry-After-Ms`.", ErrorCategory.DATABASE, 429, { retryable: true, severity: "WARNING" }),
  // Configuration / Internal
  CFG001: spec("CFG001", "Service configuration error", "An internal misconfiguration was detected; this is a server-side bug. Contact support.", ErrorCategory.CONFIGURATION, 500),
  INT001: spec("INT001", "Internal server error", "An unexpected error occurred. Retry, then contact support if persistent.", ErrorCategory.INTERNAL, 500),
  // Pipeline + data
  PIPE001: spec("PIPE001", "Pipeline processing error", "A pipeline step failed. The transaction will be retried automatically; contact support if it does not recover.", ErrorCategory.PIPELINE, 500, { retryable: true }),
  DATA001: spec("DATA001", "Data integrity error", "An expected invariant on the data was violated. This is a server-side bug; contact support.", ErrorCategory.DATA, 500)
};
function getCodeSpec(code) {
  for (const member of Object.values(ErrorCode)) {
    if (member.code === code) return member;
  }
  return null;
}
function errorCodeOf(error) {
  for (const candidate of [error.code, error.error_code]) {
    if (candidate && getCodeSpec(candidate)) return candidate;
  }
  return error.error_code ?? null;
}

// src/api/unwrap.ts
var ApiError = class extends Error {
  constructor(envelope) {
    super(envelope.error.message);
    this.envelope = envelope;
    this.name = "ApiError";
    this.statusCode = envelope.statusCode;
    this.code = errorCodeOf(envelope.error) ?? void 0;
    this.level = envelope.error.error_level;
    this.details = envelope.error.details;
    this.correlationId = envelope.error.correlation_id;
  }
  envelope;
  statusCode;
  code;
  level;
  details;
  correlationId;
};
function unwrapResponse(res) {
  if (res.ok) return res;
  throw new ApiError(res);
}
function unwrapData(res) {
  return unwrapResponse(res).data;
}

// src/overlays/batch.ts
var RESOLVE_PATH = "/addresses/catalog/resolve";
function overlayRefKey(chainId, address) {
  return `${(chainId ?? "").toLowerCase()}::${address.toLowerCase()}`;
}
var EMPTY_BATCH_OVERLAYS = {
  byKey: /* @__PURE__ */ new Map(),
  byAddress: /* @__PURE__ */ new Map(),
  rowByKey: /* @__PURE__ */ new Map(),
  rowByAddress: /* @__PURE__ */ new Map()
};
function indexBatchEntries(entries) {
  const byKey = /* @__PURE__ */ new Map();
  const byAddress = /* @__PURE__ */ new Map();
  const rowByKey = /* @__PURE__ */ new Map();
  const rowByAddress = /* @__PURE__ */ new Map();
  for (const entry of entries) {
    if (!entry.address) continue;
    const lowered = entry.address.toLowerCase();
    const keys = /* @__PURE__ */ new Set([overlayRefKey(entry.chain, entry.address)]);
    if (entry.askedChain !== void 0) keys.add(overlayRefKey(entry.askedChain, entry.address));
    for (const key of keys) byKey.set(key, entry.overlays);
    byAddress.set(lowered, entry.overlays);
    if (entry.row && entry.saved) {
      for (const key of keys) rowByKey.set(key, entry.row);
      rowByAddress.set(lowered, entry.row);
    }
  }
  return { byKey, byAddress, rowByKey, rowByAddress };
}
function lookupOverlays(batch, address, chainId) {
  if (!address) return void 0;
  if (!chainId) return batch.byAddress.get(address.toLowerCase());
  return batch.byKey.get(overlayRefKey(chainId, address));
}
function lookupAddressRow(batch, address, chainId) {
  if (!address) return void 0;
  if (!chainId) return batch.rowByAddress.get(address.toLowerCase());
  return batch.rowByKey.get(overlayRefKey(chainId, address));
}
function dedupeRefs(refs) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const ref2 of refs) {
    const address = (ref2.address ?? "").trim();
    if (!address) continue;
    const key = overlayRefKey(ref2.chainId, address);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ address, chainId: ref2.chainId ?? null });
  }
  return out;
}
function refsCacheKey(refs) {
  return refs.map((r) => overlayRefKey(r.chainId, r.address)).sort().join(",");
}
var RESOLVE_REF_LIMIT = 500;
var RESOLVE_CONCURRENCY = 3;
async function resolveChunk(client, refs, opts) {
  const body = {
    refs: refs.map((r) => ({ chain: r.chainId ?? void 0, address: r.address })),
    ...opts?.includeRow ? { include_row: true } : {},
    ...opts?.withScopes ? { scopes: true } : {},
    // `USER` is the caller's own view, which is what omitting it already means.
    // Sending it anyway would be a distinct request body for an identical read.
    ...opts?.scope && opts.scope !== "USER" ? { scope: opts.scope } : {}
  };
  const res = unwrapData(
    await client.post(RESOLVE_PATH, body, { signal: opts?.signal })
  );
  return res?.resolved ?? [];
}
function sameChain(a, b) {
  return (toCanonicalChainId(a) ?? "") === (toCanonicalChainId(b) ?? "");
}
function askedChainFor(entry, asked) {
  const chains = asked.get(entry.address.toLowerCase());
  if (!chains) return void 0;
  if (chains.length === 1) return chains[0];
  return chains.find((chain) => sameChain(chain, entry.chain));
}
async function fetchAddressOverlaysBatch(client, refs, opts) {
  const cleaned = dedupeRefs(refs);
  if (cleaned.length === 0) return EMPTY_BATCH_OVERLAYS;
  const chunks = [];
  for (let i = 0; i < cleaned.length; i += RESOLVE_REF_LIMIT) {
    chunks.push(cleaned.slice(i, i + RESOLVE_REF_LIMIT));
  }
  const entries = [];
  for (let i = 0; i < chunks.length; i += RESOLVE_CONCURRENCY) {
    const slice = chunks.slice(i, i + RESOLVE_CONCURRENCY);
    const wave = await Promise.all(slice.map((chunk) => resolveChunk(client, chunk, opts)));
    for (let c = 0; c < wave.length; c += 1) {
      const asked = /* @__PURE__ */ new Map();
      for (const ref2 of slice[c]) {
        const lowered = ref2.address.toLowerCase();
        asked.set(lowered, [...asked.get(lowered) ?? [], ref2.chainId]);
      }
      for (const entry of wave[c]) {
        if (!entry?.address) continue;
        entries.push({
          address: entry.address,
          chain: entry.chain,
          askedChain: askedChainFor(entry, asked),
          overlays: catalogBlockToResolvedOverlays(entry.catalog ?? void 0, {
            address: entry.address,
            chainId: entry.chain ?? void 0
          }),
          row: entry.row,
          saved: entry.saved
        });
      }
    }
  }
  return indexBatchEntries(entries);
}
async function resolveAddressAcrossChains(client, address, chains, opts) {
  const cleanAddress = (address ?? "").trim();
  const uniqueChains = Array.from(
    new Set(chains.map((c) => (c ?? "").trim()).filter(Boolean))
  );
  if (!cleanAddress || uniqueChains.length === 0) return [];
  const body = {
    refs: uniqueChains.map((chain) => ({ chain, address: cleanAddress })),
    include_row: true,
    ...opts?.withScopes ? { scopes: true } : {},
    ...opts?.scope && opts.scope !== "USER" ? { scope: opts.scope } : {}
  };
  const res = unwrapData(
    await client.post(RESOLVE_PATH, body, { signal: opts?.signal })
  );
  const entries = res?.resolved ?? [];
  const byChain = /* @__PURE__ */ new Map();
  entries.forEach((entry) => {
    if (entry?.chain) byChain.set(entry.chain.toLowerCase(), entry);
  });
  return uniqueChains.map((chain, i) => {
    const entry = byChain.get(chain.toLowerCase()) ?? entries[i];
    const resolvedChain = entry?.chain ?? chain;
    return {
      chain: resolvedChain,
      overlays: catalogBlockToResolvedOverlays(entry?.catalog ?? void 0, {
        address: cleanAddress,
        chainId: resolvedChain
      }),
      row: entry?.row ?? null,
      saved: Boolean(entry?.saved)
    };
  });
}

// src/overlays/resolver.ts
function claimedRisk(riskLevel) {
  const level = coerceRisk(riskLevel);
  return level && level !== RiskLevel.SAFE ? level : void 0;
}
function overlayResolver(batch) {
  return (address, chainId) => {
    const bundle = lookupOverlays(batch, address, chainId);
    if (!bundle) return void 0;
    const row = lookupAddressRow(batch, address, chainId);
    return toAddressOverlay(bundle, { riskLevel: claimedRisk(row?.risk_level) });
  };
}

// src/overlays/rest.ts
function ref(chain, address) {
  return `/addresses/${encodeURIComponent(chain)}/${encodeURIComponent(address)}`;
}
function restQuery(args) {
  const query = {};
  if (args.withScopes) query.scopes = "true";
  if (args.scope && args.scope !== "USER") query.scope = args.scope;
  return Object.keys(query).length > 0 ? query : void 0;
}
async function fetchAddressOverlaysRest(client, args, opts) {
  const chain = args.chain ?? void 0;
  if (!chain) return null;
  const res = unwrapData(
    await client.get(ref(chain, args.address), {
      query: restQuery(args),
      signal: opts?.signal,
      cacheTtlMs: 0
    })
  );
  const block = res?.catalog ?? void 0;
  return catalogBlockToResolvedOverlays(block, { address: args.address, chainId: chain });
}
async function fetchAddressWithOverlays(client, args, opts) {
  const chain = args.chain ?? void 0;
  if (!chain) return null;
  const res = unwrapData(
    await client.get(ref(chain, args.address), {
      query: restQuery(args),
      signal: opts?.signal,
      cacheTtlMs: 0
    })
  );
  return {
    address: res?.address ?? null,
    saved: Boolean(res?.saved),
    overlays: catalogBlockToResolvedOverlays(res?.catalog ?? void 0, {
      address: args.address,
      chainId: chain
    })
  };
}

// src/graphql/client.ts
var GraphQLRequestError = class extends Error {
  constructor(message, errors = [], status = 0, correlationId) {
    super(message);
    this.errors = errors;
    this.status = status;
    this.correlationId = correlationId;
    this.name = "GraphQLRequestError";
  }
  errors;
  status;
  correlationId;
};
function isGraphQLRequestError(err) {
  return err instanceof GraphQLRequestError || err instanceof Error && err.name === "GraphQLRequestError";
}

// src/graphql/operations/address.ts
var RESOLVED_ENTITY_FIELDS = (
  /* GraphQL */
  `
  id
  name
  type
  logo
  color
  confidence
  verified
`
);
var RESOLVED_HIT_CORE_FIELDS = (
  /* GraphQL */
  `
  kind
  catalogId
  name
  color
  logo
  tierWeight
  isPrimary
  severity
  riskLevel
  confidence
  lifecycle
  explanation
`
);
var RESOLVED_HIT_FIELDS = (
  /* GraphQL */
  `
  ${RESOLVED_HIT_CORE_FIELDS}
  availableScopes {
    scope
    tierWeight
    overrides {
      key
      value
    }
  }
`
);
function resolvedBundleFields(hitFields) {
  return (
    /* GraphQL */
    `
    address
    chainId
    asOf
    entities { ${RESOLVED_ENTITY_FIELDS} }
    tags { ${hitFields} }
    labels { ${hitFields} }
    groups { ${hitFields} }
    primary {
      kind
      catalogId
    }
  `
  );
}
var clean2 = (v) => v == null ? void 0 : v;
function mapScope(scope) {
  const overrides = {};
  for (const { key, value } of scope.overrides ?? []) overrides[key] = value;
  return { scope: scope.scope, tierWeight: scope.tierWeight, overrides };
}
function mapHit(hit) {
  return {
    kind: hit.kind,
    catalogId: hit.catalogId,
    name: clean2(hit.name),
    color: clean2(hit.color),
    logo: clean2(hit.logo),
    tierWeight: hit.tierWeight,
    isPrimary: hit.isPrimary,
    severity: clean2(hit.severity),
    riskLevel: clean2(hit.riskLevel),
    confidence: clean2(hit.confidence),
    lifecycle: clean2(hit.lifecycle),
    explanation: hit.explanation ?? [],
    availableScopes: (hit.availableScopes ?? []).map(mapScope)
  };
}
function mapEntity(entity) {
  return {
    id: entity.id,
    name: clean2(entity.name),
    type: clean2(entity.type),
    logo: clean2(entity.logo),
    color: clean2(entity.color),
    confidence: clean2(entity.confidence),
    verified: entity.verified ?? void 0
  };
}
function mapBundle(a) {
  return {
    address: a.address,
    chainId: clean2(a.chainId),
    asOf: a.asOf,
    entities: (a.entities ?? []).map(mapEntity),
    tags: (a.tags ?? []).map(mapHit),
    labels: (a.labels ?? []).map(mapHit),
    groups: (a.groups ?? []).map(mapHit),
    primary: a.primary ?? []
  };
}

// src/graphql/operations/addressDetail.ts
var ADDRESS_ROW_FIELDS = `
  address
  chain
  addressType
  isPrimaryChain
  name
  notes
  symbol
  decimals
  isContract
  deployer
  deployedAt
  lastSeenAt
  riskLevel
  severity
  lifecycle
  isHumanApproved
  enrichmentStatus
  primaryEntityId
  logoS3Uri
  aliases
  source
  confidence
  systemDefault
  metadata
`;
function mapAddressRow(row) {
  return {
    address: row.address,
    chain: row.chain,
    address_type: row.addressType ?? void 0,
    is_primary_chain: Boolean(row.isPrimaryChain),
    name: row.name ?? null,
    notes: row.notes ?? null,
    symbol: row.symbol ?? null,
    decimals: row.decimals ?? null,
    is_contract: row.isContract ?? null,
    deployer: row.deployer ?? null,
    deployed_at: row.deployedAt ?? null,
    last_seen_at: row.lastSeenAt ?? null,
    risk_level: row.riskLevel ?? void 0,
    severity: row.severity ?? void 0,
    lifecycle: row.lifecycle ?? void 0,
    // The pair `isVerified`/`isBeingMapped` read. Projected here so the detail
    // panel judges a row the same way whichever mouth answered it.
    is_human_approved: Boolean(row.isHumanApproved),
    enrichment_status: row.enrichmentStatus ?? null,
    primary_entity_id: row.primaryEntityId ?? null,
    logo_s3_uri: row.logoS3Uri ?? null,
    aliases: row.aliases ?? [],
    source: row.source ?? null,
    confidence: row.confidence ?? void 0,
    system_default: Boolean(row.systemDefault),
    metadata: row.metadata ?? {}
  };
}

// src/graphql/operations/addressBatch.ts
var GRAPHQL_BATCH_REF_LIMIT = 50;
function addressBatchRequest(refs, opts = {}) {
  const bundle = resolvedBundleFields(opts.withScopes ? RESOLVED_HIT_FIELDS : RESOLVED_HIT_CORE_FIELDS);
  const variables = {
    scope: opts.scope && opts.scope !== "USER" ? opts.scope : void 0
  };
  const declarations = ["$scope: AddressScope"];
  const fields = [];
  const withRow = [];
  refs.forEach((ref2, i) => {
    const chain = ref2.chainId || void 0;
    const row = Boolean(opts.includeRow && chain);
    variables[`a${i}`] = ref2.address;
    variables[`c${i}`] = chain;
    withRow.push(row);
    if (row) {
      declarations.push(`$a${i}: String!`, `$c${i}: String!`);
      fields.push(
        `r${i}: addressDetail(address: $a${i}, chainId: $c${i}, scope: $scope, withScopes: $withScopes) {
          saved
          row { ${ADDRESS_ROW_FIELDS} }
          catalog { ${bundle} }
        }`
      );
    } else {
      declarations.push(`$a${i}: String!`, `$c${i}: String`);
      fields.push(`r${i}: address(address: $a${i}, chainId: $c${i}, scope: $scope) { ${bundle} }`);
    }
  });
  if (withRow.some(Boolean)) {
    declarations.push("$withScopes: Boolean");
    variables.withScopes = Boolean(opts.withScopes);
  }
  return {
    query: `query AddressBatch(${declarations.join(", ")}) {
${fields.join("\n")}
}`,
    variables,
    withRow
  };
}
async function graphqlResolveAddressesBatch(client, refs, opts = {}, requestOpts) {
  const cleaned = dedupeRefs(refs);
  if (cleaned.length === 0) return EMPTY_BATCH_OVERLAYS;
  if (cleaned.length > GRAPHQL_BATCH_REF_LIMIT) {
    throw new RangeError(
      `graphqlResolveAddressesBatch: ${cleaned.length} refs exceeds ${GRAPHQL_BATCH_REF_LIMIT}; use the REST batch`
    );
  }
  const { query, variables, withRow } = addressBatchRequest(cleaned, opts);
  const data = await client.request(query, variables, requestOpts);
  const entries = cleaned.map((ref2, i) => {
    const empty = () => catalogBlockToResolvedOverlays(void 0, { address: ref2.address, chainId: ref2.chainId ?? void 0 });
    if (withRow[i]) {
      const detail = data[`r${i}`] ?? null;
      return {
        address: ref2.address,
        askedChain: ref2.chainId,
        chain: detail?.catalog?.chainId ?? detail?.row?.chain ?? ref2.chainId,
        overlays: detail?.catalog ? mapBundle(detail.catalog) : empty(),
        row: detail?.row ? mapAddressRow(detail.row) : null,
        saved: Boolean(detail?.saved)
      };
    }
    const bundle = data[`r${i}`] ?? null;
    return {
      address: ref2.address,
      askedChain: ref2.chainId,
      chain: bundle?.chainId ?? ref2.chainId,
      overlays: bundle ? mapBundle(bundle) : empty()
    };
  });
  return indexBatchEntries(entries);
}

// src/overlays/batchRouted.ts
var log = createLogger("fe-commons:overlays");
async function fetchAddressOverlaysBatchRouted(client, refs, route, opts = {}) {
  const { graphqlClient } = route;
  if (route.transport === "graphql" && graphqlClient && dedupeRefs(refs).length <= GRAPHQL_BATCH_REF_LIMIT) {
    try {
      return await graphqlResolveAddressesBatch(graphqlClient, refs, opts, { signal: opts.signal });
    } catch (err) {
      if (!isGraphQLRequestError(err)) throw err;
      log.warn("address.overlays batch GraphQL failed, falling back to REST", {
        status: err.status,
        message: err.message
      });
    }
  }
  return fetchAddressOverlaysBatch(client, refs, opts);
}

// src/overlays/txCatalog.ts
var RESOLVE_PATH2 = "/transactions/catalog/resolve";
var RESOLVE_REF_LIMIT2 = 500;
var RESOLVE_CONCURRENCY2 = 3;
var EMPTY_TX_CATALOG_BATCH = { byTxId: /* @__PURE__ */ new Map() };
function lookupTxCatalog(batch, txId) {
  return txId ? batch.byTxId.get(txId) : void 0;
}
function dedupeTxRefs(refs) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const ref2 of refs) {
    const txId = (ref2.txId ?? "").trim();
    if (!txId || seen.has(txId)) continue;
    seen.add(txId);
    out.push({ txId, chainId: ref2.chainId ?? null });
  }
  return out;
}
async function resolveChunk2(client, refs, opts) {
  const body = {
    refs: refs.map((r) => ({ tx_id: r.txId, ...r.chainId ? { chain_id: r.chainId } : {} })),
    ...opts?.withScopes ? { scopes: true } : {},
    // `USER` is the caller's own view, which is what omitting it already means.
    // Sending it anyway would be a distinct request body for an identical read.
    ...opts?.scope && opts.scope !== "USER" ? { scope: opts.scope } : {}
  };
  const res = unwrapData(
    await client.post(RESOLVE_PATH2, body, { signal: opts?.signal })
  );
  return res?.resolved ?? [];
}
async function fetchTxCatalogBatch(client, refs, opts) {
  const cleaned = dedupeTxRefs(refs);
  if (cleaned.length === 0) return EMPTY_TX_CATALOG_BATCH;
  const chunks = [];
  for (let i = 0; i < cleaned.length; i += RESOLVE_REF_LIMIT2) {
    chunks.push(cleaned.slice(i, i + RESOLVE_REF_LIMIT2));
  }
  const byTxId = /* @__PURE__ */ new Map();
  for (let i = 0; i < chunks.length; i += RESOLVE_CONCURRENCY2) {
    const wave = await Promise.all(
      chunks.slice(i, i + RESOLVE_CONCURRENCY2).map((chunk) => resolveChunk2(client, chunk, opts))
    );
    for (const entries of wave) {
      for (const entry of entries) {
        if (!entry?.tx_id) continue;
        byTxId.set(
          entry.tx_id,
          catalogBlockToResolvedOverlays(entry.catalog ?? void 0, { address: entry.tx_id })
        );
      }
    }
  }
  return { byTxId };
}

// src/attribution/scope.ts
var PHASE_1_RESOLVE_SCOPES = ["USER", "TENANT", "GLOBAL"];
var PHASE_1_WRITE_SCOPES = ["private", "team", "tenant"];
var PHASE_1_DEFAULT_WRITE_SCOPE = "tenant";
var PHASE_1_DEFAULT_RESOLVE_SCOPE = "USER";
function resourcePolicy(overrides = {}) {
  return {
    enabled: true,
    resolvedView: true,
    showProvenance: true,
    showLogos: true,
    resolveScopes: PHASE_1_RESOLVE_SCOPES,
    writeScopes: PHASE_1_WRITE_SCOPES,
    defaultWriteScope: PHASE_1_DEFAULT_WRITE_SCOPE,
    defaultResolveScope: PHASE_1_DEFAULT_RESOLVE_SCOPE,
    configurableWriteDefault: true,
    configurableReadDefault: true,
    ...overrides
  };
}
var DISABLED_RESOURCE_POLICY = {
  enabled: false,
  resolvedView: false,
  showProvenance: false,
  showLogos: false,
  resolveScopes: [],
  writeScopes: ["private"],
  defaultWriteScope: "private",
  defaultResolveScope: "USER",
  configurableWriteDefault: false,
  configurableReadDefault: false
};
function attributionPolicies(overrides = {}) {
  const base = {
    // Transactions already receive a fully-resolved payload (with alternatives)
    // from `transactions-mgmt-api`, so the widen ladder is safe to show in full.
    // The WRITE axis still takes no saved default: the patch route carries no
    // ABAC fields, so a transaction overlay is tenant-wide by construction and
    // there is nothing to choose. The READ axis does, now that the batch resolve
    // takes a tier — while it could not, a saved default would have applied to a
    // transaction's detail page and not to the same transaction's row in a table.
    transaction: resourcePolicy({
      configurableWriteDefault: false,
      configurableReadDefault: true
    }),
    address: resourcePolicy(),
    entity: resourcePolicy(),
    label: resourcePolicy(),
    tag: resourcePolicy(),
    group: resourcePolicy(),
    // Protocol definitions are shared decoding machinery, so the two personal
    // rungs are deliberately absent: `private` / `team` would mean "decode this
    // protocol differently for just me", which makes one tenant's transactions
    // irreproducible for their colleagues. Tenant-wide is the narrowest rung
    // that keeps a decode explainable to everyone who reads it, and it stays the
    // default.
    //
    // The two rungs ABOVE it are offered, and were missing rather than excluded:
    // the reasoning above rules out going narrower than a tenant and says
    // nothing about going wider, while the backend has always accepted
    // `customer` / `org` / `tenant` / `user` and the store folds all four. A
    // customer running several orgs on one decoder had to author the same patch
    // once per tenant and keep them in step by hand — which is the drift the
    // ladder exists to remove.
    //
    // `system` is still absent, and that is a real exclusion: the system rung is
    // a published definition rather than an overlay, and the API refuses a patch
    // there (`POST /protocols/publish` is its own internal-only door).
    protocol: resourcePolicy({
      writeScopes: ["tenant", "org", "customer"],
      defaultWriteScope: "tenant",
      configurableWriteDefault: false,
      configurableReadDefault: false
    }),
    // -- The authored records (see RECORD_RESOURCES) ----------------------
    //
    // Every default below is what the surface ALREADY did before it was asked
    // to consult this table, and deliberately so: extending the setting must
    // give people a choice, not silently move anyone's rows. That is why these
    // five do not share one default — a grouping rule has always been
    // tenant-wide and a tracer has always been owner-only, and unifying them
    // here would have re-homed live rows on deploy.
    //
    // None carries a read axis: there is no precedence stack to resolve, so
    // `resolvedView` is off and the read card never lists them.
    // Rung on the row + team carriers, so the whole ladder is expressible.
    tracer: resourcePolicy({
      resolvedView: false,
      configurableReadDefault: false,
      defaultWriteScope: "private",
      writeScopes: ["private", "team", "tenant", "org", "customer"]
    }),
    // The odd one out, and the reason `writeScopes` is per-resource: an alert
    // rule has NO tenancy rung on the wire (`ruleToWire` sends `condition`,
    // `destination_ids` and `visible_to_team_ids`, and nothing else about who
    // may see it). Owner-only and team-shared are the only two states it can
    // hold, so those are the only two offered — a `tenant` option here would
    // collect a choice the route discards, which is worse than no option.
    alertRule: resourcePolicy({
      resolvedView: false,
      configurableReadDefault: false,
      defaultWriteScope: "private",
      writeScopes: ["private", "team"]
    }),
    // No `team`, and that is a wire fact rather than a preference: `toWire`
    // sends `scope` and nothing else about audience, so a team choice would be
    // dropped in the adapter. A `team` rung was offered on this resource once
    // and `_applies_to` narrowed only `user` rows — a "team" rule was in force
    // tenant-wide while ranking above the tenant rule it shadowed. It is not
    // coming back through a default.
    groupingRule: resourcePolicy({
      resolvedView: false,
      configurableReadDefault: false,
      defaultWriteScope: "tenant",
      writeScopes: ["private", "tenant", "org", "customer"]
    }),
    // Same shape: `save` posts a `scope` and no carrier.
    pipeline: resourcePolicy({
      resolvedView: false,
      configurableReadDefault: false,
      defaultWriteScope: "private",
      writeScopes: ["private", "tenant", "org", "customer"]
    }),
    // `org` / `customer` are real rungs on the backend enum and `_guard_scope`
    // admits them with the write verb, but the save dialog has only ever
    // offered Personal and Shared. Kept to what the product offers rather than
    // to what the wire accepts: widening is one line here the day the dialog
    // grows the options, and offering a default nobody can pick at attach time
    // is the asymmetry this table exists to avoid.
    // Personal and Shared, the two the save dialog offers. No `team` even
    // though `ViewScope` has the rung: `viewToWire` sends no `visible_to_team_ids`,
    // and the backend's tier scopes (which carry their audience in the tenancy
    // tuple) exclude TEAM — so a team-scoped view would depend on carriers this
    // client never writes. `org` / `customer` are real and admitted with the
    // write verb; they wait for the dialog to offer them.
    savedView: resourcePolicy({
      resolvedView: false,
      configurableReadDefault: false,
      defaultWriteScope: "private",
      writeScopes: ["private", "tenant"]
    })
  };
  const out = { ...base };
  for (const [resource, override] of Object.entries(overrides)) {
    const key = resource;
    if (override) out[key] = { ...out[key] ?? DISABLED_RESOURCE_POLICY, ...override };
  }
  return out;
}
attributionPolicies();
function tierWeightToScope(tierWeight) {
  if (tierWeight >= 60) return "user";
  if (tierWeight >= 40) return "tenant";
  if (tierWeight > 30) return "org";
  if (tierWeight >= 30) return "default";
  if (tierWeight >= 20) return "entity";
  return "provider";
}

// src/overlays/bundle.ts
function hitLabel(hit) {
  return hit.name ?? hit.catalogId;
}
function primaryFirst(hits) {
  return [...hits].sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
}
function glyph(hit) {
  return { logo: hit.logo, color: hit.color };
}
function why(hit) {
  return {
    confidence: hit.confidence,
    explanation: hit.explanation.length > 0 ? hit.explanation : void 0,
    lifecycle: hit.lifecycle
  };
}
function overlayLabelRefs(hits) {
  return primaryFirst(hits).map((hit) => ({
    label: hitLabel(hit),
    // The provenance dot: which tier won this overlay (yours / your tenant's / a
    // provider's). Same derivation as the chips, so a row agrees with the panel.
    scope: tierWeightToScope(hit.tierWeight),
    // Two different questions about the same chip: `scope` is who says so,
    // `severity` is how bad it is. A label answers both.
    ...severityRef(hit),
    ...glyph(hit),
    ...why(hit)
  }));
}
function overlayTagRefs(hits) {
  return primaryFirst(hits).map((hit) => ({
    label: hitLabel(hit),
    ...severityRef(hit),
    ...glyph(hit),
    ...why(hit)
  }));
}
function severityRef(hit) {
  const key = typeof hit.severity === "string" ? hit.severity.toLowerCase() : "";
  return Object.values(Severity).includes(key) ? { severity: key } : {};
}
function overlayGroupRefs(hits) {
  return primaryFirst(hits).map((hit) => ({
    id: hit.catalogId,
    name: hit.name,
    ...glyph(hit),
    ...why(hit)
  }));
}
function overlayEntityRef(overlays) {
  const [first] = overlays.entities;
  if (!first) return null;
  return {
    id: first.id,
    name: first.name ?? first.id,
    entityType: first.type,
    logo: first.logo,
    color: first.color,
    // An address→entity link is scored like any other assignment; the bundle
    // carries no bullets for it, so the hover states the score alone.
    confidence: first.confidence
  };
}
function overlayCatalogBundle(overlays) {
  return {
    labels: overlayLabelRefs(overlays.labels),
    tags: overlayTagRefs(overlays.tags),
    groups: overlayGroupRefs(overlays.groups),
    entity: overlayEntityRef(overlays)
  };
}
function catalogBundleChips(bundle) {
  return {
    entities: bundle.entity ? [entityChip(bundle.entity)] : [],
    labels: (bundle.labels ?? []).map(labelChip),
    tags: (bundle.tags ?? []).map(tagChip),
    groups: (bundle.groups ?? []).map(groupChip)
  };
}
function textChipId(label, index) {
  return `${label}-${index}`;
}
function labelChip(ref2, index) {
  return {
    id: textChipId(ref2.label, index),
    name: ref2.label,
    value: ref2.value,
    // Two different questions about one chip: `scope` is who says so, `severity`
    // is how bad it is. A label answers both — an explorer's phishing or
    // sanctions claim arrives as a label as often as a tag, and a row that drops
    // it draws `Sanctioned: OFAC` exactly like `Uniswap: Router`.
    scope: ref2.scope,
    severity: ref2.severity,
    ...glyphOf(ref2),
    ...whyOf(ref2)
  };
}
function tagChip(ref2, index) {
  return {
    id: textChipId(ref2.label, index),
    name: ref2.label,
    severity: ref2.severity,
    ...glyphOf(ref2),
    ...whyOf(ref2)
  };
}
function groupChip(ref2) {
  return {
    id: ref2.id,
    // A group IS identified by id, so an unnamed one shows the id rather than
    // an empty chip.
    name: ref2.name ?? ref2.id,
    count: ref2.count,
    ...glyphOf(ref2),
    ...whyOf(ref2)
  };
}
function entityChip(ref2) {
  return {
    id: ref2.id,
    name: ref2.name || ref2.id,
    entityType: ref2.entityType,
    isPrimary: ref2.isPrimary,
    ...glyphOf(ref2),
    ...whyOf(ref2)
  };
}
function glyphOf(ref2) {
  return { logo: ref2.logo, color: ref2.color };
}
function whyOf(ref2) {
  return { confidence: ref2.confidence, explanation: ref2.explanation, lifecycle: ref2.lifecycle };
}

export { EMPTY_BATCH_OVERLAYS, EMPTY_TX_CATALOG_BATCH, addressRisk, catalogBlockToResolvedOverlays, catalogBundleChips, catalogRisk, declaredRisk, dedupeRefs, dedupeTxRefs, fetchAddressOverlaysBatch, fetchAddressOverlaysBatchRouted, fetchAddressOverlaysRest, fetchAddressWithOverlays, fetchTxCatalogBatch, indexBatchEntries, isEmptyOverlays, lookupAddressRow, lookupOverlays, lookupTxCatalog, mapWireHit, overlayCatalogBundle, overlayEntityRef, overlayGroupRefs, overlayLabelRefs, overlayRefKey, overlayResolver, overlayRisk, overlayTagRefs, refsCacheKey, resolveAddressAcrossChains, riskForHit, riskFromSeverity, scoreFromSeverity, toAddressOverlay, toResolvedOverlays };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map